'use client'

import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'next/navigation'
import { memo, useState, type FC, useCallback, useMemo, useRef, useEffect } from 'react'

import { getGetUserQueryKey, getUser, type UserDetailed } from '@api/orval/apiHooks'
import { orvalFetch } from '@api/orvalFetcher'
import { SearchSelect, type SearchSelectOption } from '@fp/ui-kit'
import { useDebouncedValue } from '@hooks/useDebounce'
import { formatNameWithLastInitial } from '@lib/textFormatters'

export type UserId = number | null
export type UserType = Partial<UserDetailed> & {
  id: UserId
  first_name: string
  last_name: string
  email: string | null
}
type DisplayType = 'email' | 'name'

type MetadataPathKey = 'user_search_path' | 'processed_by_users_path' | 'added_by_users_path' | 'assignee_search_path'
type UrlUserId = 'filter_user_id' | 'processed_by_user_id' | 'added_by_user_id' | 'assignee_id' | string

/*
 * Input with dropdown component
 * 
 * Step:
 * 0 - get the user label from the cache
 * 1 - get the search path from the metadata
 * 2 - query the search path with the debounced input text
 * 3 - render dropdown with the search results
 * 
 * props: {
 *   onSelect - used for updating the parent form state
 *   useMetadataHook - used for getting the search path from the metadata
 *   placeholder - used for the input placeholder
 *   pathKey - used for the search path key from the metadata
 *   onInteractOutside - used for closing the dropdown when interaction is finished (e.g., clicking outside)
 *   isAutoOpen - used for opening the dropdown automatically when the component is mounted
 *   type - used for the display type (email or name)
 *   preFetchOnChangeInputOnly - used for pre-fetching the data only when the input is changed
 *   additionalOptions - used for additional options (e.g., unassigned user)
 * }
 *
 Example usage: 
   // Default type is 'email'
   <InputWithDropdown
    onSelect={fieldProps.onChange} // get from react-hook-form
    useMetadataHook={useGetUsersFilterMetadata} // get from generated orval hooks
    placeholder='Search by user'
    metadataPathKey='user_search_path'
    urlUserId='filter_user_id'
    additionalOptions = [{ id: null, first_name: 'Unassigned', last_name: '', email: 'Unassigned' }],
   />
   
   // 'name' type
    <InputWithDropdown
      placeholder='Select Assignee'
      onSelect={onSelect}
      useMetadataHook={useGetPayoutsFilterMetadata}
      urlUserParam='assignee_search_path'
      initialSearchId={row.original.assignee?.id}
      type='name'
      isAutoOpen
      onInteractOutside={onClose}
    />
*/

interface InputWithDropdownProps {
  /** Callback to update the parent form state when a user is selected or cleared. */
  onSelect: (id: UserId) => void
  /** A react-query hook that returns metadata containing a `user_search_path`. */
  useMetadataHook?: () => { data?: { fields?: Partial<Record<MetadataPathKey, string>> }; isPending?: boolean }
  searchPath?: string
  placeholder?: string
  metadataPathKey?: MetadataPathKey

  /** The parameter key for the user ID lookup in the URL on page load. */
  urlUserParam?: UrlUserId
  /** The user ID to be used as the initial search ID. InitialSearchId has more priority than urlUserParam. */
  initialSearchId?: number | null

  type?: DisplayType
  preFetchOnChangeInputOnly?: boolean
  additionalOptions?: UserType[]

  /** Whether to open the dropdown automatically when the component is mounted. */
  isAutoOpen?: boolean

  /** Callback to trigger when interaction is finished (e.g., clicking outside). */
  onInteractOutside?: () => void
}

const REQUEST_LIMIT = 10
const DEBOUNCE_MS = 300
const STALE_TIME = 1000 * 60 * 60 * 24 // 1 day
const INTERACT_OUTSIDE_DELAY_MS = 500

const getUserIdFromURL = (userIdKey: string) => {
  const urlParams = new URLSearchParams(window.location.search)
  const userId = urlParams.get(userIdKey)

  return userId ? Number(userId) : null
}

export const getStringByType = (type: DisplayType, data?: Partial<UserType>): string | null => {
  const { email, first_name, last_name, name, full_kyc_name } = data ?? {}

  if (type === 'email') {
    return email || null
  }

  if (type === 'name') {
    if (first_name && last_name) {
      return formatNameWithLastInitial(first_name, last_name)
    }

    return name || full_kyc_name || null
  }

  return 'invalid_type'
}

const getUserLabelFromCache = (qc: QueryClient, userId: UserId, type: DisplayType): string | null => {
  if (typeof userId !== 'number' || isNaN(userId)) return null

  const key = getGetUserQueryKey(userId)
  const cachedData = qc.getQueryData<UserDetailed>(key)

  return cachedData ? getStringByType(type, cachedData) : null
}

const fetchUserLabel = async (qc: QueryClient, userId: number, type: DisplayType) => {
  const cached = getUserLabelFromCache(qc, userId, type)
  if (cached) return cached

  const key = getGetUserQueryKey(userId)
  const data = await qc.fetchQuery({
    queryKey: key,
    queryFn: ({ signal }) => getUser(userId, {}, signal),
    staleTime: STALE_TIME,
  })

  return getStringByType(type, data)
}

const userToOption = (user: UserType): SearchSelectOption<UserId> => ({
  id: user.id,
  label: `${user.first_name} ${user.last_name}`.trim(),
  description: user.email,
})

export const InputWithDropdown: FC<InputWithDropdownProps> = memo(
  ({
    onSelect,
    useMetadataHook,
    searchPath,
    placeholder,
    metadataPathKey = 'user_search_path',
    urlUserParam = 'filter_user_id',
    initialSearchId = undefined,
    isAutoOpen = false,
    type = 'email',
    preFetchOnChangeInputOnly = false,
    additionalOptions = [],
    onInteractOutside,
  }) => {
    const qc = useQueryClient()
    const lastSearchIdRef = useRef<UserId>(null)
    const isUserTypedRef = useRef(preFetchOnChangeInputOnly)
    const inputRef = useRef<HTMLInputElement>(null)

    const initialIdRef = useRef<UserId>(initialSearchId ?? getUserIdFromURL(urlUserParam) ?? null)

    const [open, setOpen] = useState(isAutoOpen)
    const [searchId, setSearchId] = useState<UserId>(initialIdRef.current)
    const [inputText, setInputText] = useState<string | null>(() => getUserLabelFromCache(qc, searchId, type))
    const [userOptions, setUserOptions] = useState<UserType[]>([])

    const debounceInputText = useDebouncedValue(inputText, DEBOUNCE_MS) || ''

    useEffect(() => {
      if (isAutoOpen && inputRef.current) {
        inputRef.current.focus()
      }
    }, [isAutoOpen])

    const handleInteractOutside = useCallback(() => {
      // Delay to give time to process API calls before unmounting the component
      setTimeout(() => onInteractOutside?.(), INTERACT_OUTSIDE_DELAY_MS)
    }, [onInteractOutside])

    const optionsWithAdditions = useMemo(() => [...additionalOptions, ...userOptions], [additionalOptions, userOptions])

    const searchSelectOptions = useMemo(() => optionsWithAdditions.map(userToOption), [optionsWithAdditions])

    const handleSelect = useCallback(
      (id: UserId | null) => {
        if (id === null) {
          setInputText('')
          setSearchId(null)
          onSelect(null)

          return
        }

        const user = optionsWithAdditions.find((u) => u.id === id)
        const label = getStringByType(type, user)
        lastSearchIdRef.current = id
        setInputText(label)
        setSearchId(id)
        onSelect(id)
        setOpen(false)
        handleInteractOutside()
      },
      [optionsWithAdditions, onSelect, type, handleInteractOutside],
    )

    const handleInputChange = useCallback(
      (text: string) => {
        isUserTypedRef.current = true
        setInputText(text)
        setOpen(true)

        if (text === '') {
          onSelect(null)
        }
      },
      [onSelect],
    )

    useEffect(() => {
      // Ids picked from the dropdown already carry their label, only the initial one needs a lookup
      if (lastSearchIdRef.current === searchId) {
        return
      }
      if (searchId !== initialIdRef.current) {
        return
      }

      lastSearchIdRef.current = searchId

      const initInput = async () => {
        if (!searchId) {
          setInputText('')
          setSearchId(null)
          onSelect(null)
        }

        if (typeof searchId === 'number') {
          const userEmail = await fetchUserLabel(qc, searchId, type)
          setInputText(userEmail)
        }
      }

      initInput()
    }, [qc, searchId, type, onSelect])

    // Track URLSearchParams changes and clear input if urlUserParam is absent
    const searchParams = useSearchParams()
    const prevSearchParamsCacheRef = useRef<string | null>(null)
    useEffect(() => {
      if (!searchParams) return

      const current = searchParams.toString()

      if (prevSearchParamsCacheRef.current === null) {
        prevSearchParamsCacheRef.current = current

        return
      }

      if (current !== prevSearchParamsCacheRef.current) {
        const hasUserParam = searchParams.get(urlUserParam)
        if (!hasUserParam && prevSearchParamsCacheRef.current?.includes(urlUserParam)) {
          setInputText('')
          setSearchId(null)
        }
        prevSearchParamsCacheRef.current = current
      }
    }, [searchParams, urlUserParam])

    // Step 1: get the search path from metadata or use the prop
    const metadataResult = useMetadataHook?.()
    const isMetadataPending = metadataResult?.isPending ?? false

    const metadataPath = useMemo(() => {
      if (searchPath) {
        return searchPath
      }

      return metadataResult?.data?.fields?.[metadataPathKey]
    }, [searchPath, metadataResult?.data, metadataPathKey])

    const isQueryEnabled =
      !isMetadataPending &&
      typeof metadataPath === 'string' &&
      typeof debounceInputText === 'string' &&
      isUserTypedRef?.current

    // Step 2: query the search path with the debounced input text
    const {
      data: searchData,
      isFetching,
      isPending,
    } = useQuery({
      queryKey: ['user-lookup', metadataPath, debounceInputText],
      queryFn: async ({ signal }) =>
        orvalFetch<{ collection: UserType[] }>({
          url: metadataPath!,
          method: 'GET',
          params: { search_field: debounceInputText, limit: REQUEST_LIMIT },
          signal,
        }),
      enabled: isQueryEnabled,
      retry: false,
      staleTime: STALE_TIME,
    })

    const isUserOptionsLoading = useMemo(
      () => isQueryEnabled && (isFetching || isPending),
      [isFetching, isPending, isQueryEnabled],
    )
    useEffect(() => {
      if (!isUserOptionsLoading) {
        setUserOptions(searchData?.collection ?? [])
      }
    }, [isUserOptionsLoading, searchData?.collection])

    const emptyText = isUserTypedRef.current ? 'No results' : 'Type to search...'

    return (
      <SearchSelect<UserId>
        value={searchId}
        inputText={inputText || ''}
        onInputChange={handleInputChange}
        onSelect={handleSelect}
        options={searchSelectOptions}
        open={open}
        onOpenChange={setOpen}
        placeholder={placeholder}
        isLoading={isUserOptionsLoading}
        isPending={isMetadataPending}
        emptyText={emptyText}
        onInteractOutside={handleInteractOutside}
        inputRef={inputRef}
      />
    )
  },
)
